<template>
  <Notification v-if="mediaErrorMessage" class="margin-bottom-xs" severity="error" role="alert" :closable="true" @close="removeError">
    {{ mediaErrorMessage }}
  </Notification>
  <div class="media-upload grid-x">
    <div class="media-blank-container cell grid-x grid-margin-x">
      <div class="media-placeholder media-common-style cell small-4 medium-2"></div>
    </div>
    <div class="media-upload-container cell grid-x grid-margin-x">
      <div v-if="video" class="uploaded-media media-common-style cell small-4 medium-2">
        <Icon v-if="thumbnailFailed" :icon="csgConfirmationWithoutCircleGreen" size="m" aria-label="Video uploaded successfully" />
        <img
          v-else
          class="media-common-style video-thumbnail"
          :class="{ 'review-mcom': isMcom }"
          :src="video.thumbnailUrl"
          alt="Uploaded video"
          @error="thumbnailFailed = true"
        />
        <button type="button" class="close-btn-review" :class="removeVideoBtnClassBindings" @click="removeVideo"></button>
      </div>
      <div v-if="isLoading" class="media-placeholder media-common-style cell small-4 medium-2">
        <Spinner />
      </div>
      <label v-if="!video && !isLoading" for="vid_upload" class="cell small-4 medium-2" aria-label="Upload video for your review">
        <div class="upload-media-button media-common-style primary">
          <span class="add-dark-medium"></span>
          <input
            id="vid_upload"
            type="file"
            name="vid_upload"
            :accept="ACCEPTABLE_VIDEO_FORMATS_INPUT"
            :disabled="isLoading"
            @change="onFileSelected"
          />
        </div>
      </label>
    </div>
    <div class="media-helper-subtext small margin-top-xxs">
      <p>Add up to 1 video <span v-if="video">(0 remaining)</span></p>
      <p>File size must be {{ VIDEO_LIMITS.VIDEO_SIZE_LIMIT }} MB or less</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, ref } from 'vue';
import Spinner from '@atomic-ui/spinner/Spinner';
import Icon from '@atomic-ui/icon/Icon';
import Notification from '@atomic-ui/notification/Notification';
import { VIDEO_LIMITS, ACCEPTABLE_VIDEO_FORMATS_INPUT } from '~/modules/reviews/components/writeReview/constants';
import { useProduct } from '~/modules/product/composables/useProduct';
import useReviewVideoUpload from '~/modules/reviews/composables/useReviewVideoUpload';
import { useBrand } from '~/composables/useBrand';
import { csgConfirmationWithoutCircleGreen } from '@csgSvgIcons/icons';

const { productId } = useProduct();
const emit = defineEmits(['update-video', 'is-loading']);

const { isMcom } = useBrand();

const removeVideoBtnClassBindings = {
  'close-dark-large': isMcom,
  'review-mcom': isMcom,
  'close-dark-medium': !isMcom,
  'review-bcom': !isMcom,
};

const { video, isLoading, mediaErrorMessage, uploadVideo, removeVideo, validateVideo } = useReviewVideoUpload({ productId });

const thumbnailFailed = ref(false);

watch(video, (newVideo) => {
  thumbnailFailed.value = false;
  emit('update-video', newVideo);
});

watch(isLoading, (loading) => {
  emit('is-loading', loading);
});

const onFileSelected = async (event: Event) => {
  removeError();
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    const isValid = await validateVideo(file);
    if (isValid) {
      await uploadVideo(file);
    }
  }

  target.value = '';
};

function removeError() {
  mediaErrorMessage.value = '';
}
</script>







<style lang="scss" scoped>
@import '~@csgIcons/new/close-dark-large';


@import '@core/csg/src/scss/forms/forms.scss';
@import '~@csgIcons/new/add-dark-medium';
@import '~@csgIcons/new/error-red-medium';
@import '~@csgIcons/new/close-dark-small';

@mixin media-common {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  aspect-ratio: 1/1;
  border-width: 1px;
  @include breakpoint(large) {
    min-width: rem-calc(98);
  }
}
.media-error-container {
  border: rem-calc(1) solid $error-color;
  .media-error {
    display: flex;
    align-items: flex-start;
    color: $error-color;
    .error-icon {
      flex-shrink: 0;
    }
  }
  .error-message-body {
    align-self: center;
    flex-grow: 1;
  }
  .close-dark-small {
    flex-shrink: 0;
    cursor: pointer;
  }
}
.media-upload {
  position: relative;
  label {
    cursor: pointer;
    &.is-loading {
      cursor: wait;
    }
    .upload-media-button.primary {
      @include media-common;
      border-style: solid;
      border-color: $black;
      flex-direction: column;
      cursor: pointer;
      &:focus-within {
        border-width: 2px;
      }
    }
  }
  .media-blank-container {
    position: absolute;

    @include breakpoint(small only) {
      margin: 0;
      justify-content: space-between;
    }
  }
  .media-upload-container {
    z-index: 10;

    @include breakpoint(small only) {
      margin-left: 0;
    }
  }
  .media-placeholder {
    @include media-common;
    border-style: dashed;
    border-color: $gray-1-color;
    position: relative;
  }

  .uploaded-media {
    @include media-common;
    border-style: solid;
    border-color: $gray-2-color;
    position: relative;
    .review-mcom {
      border-radius: 0.25rem;
    }

    .video-thumbnail {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .review-bcom {
      background-color: #f9f9f9;
    }

    .close-btn-review {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      border-radius: 50%;
      cursor: pointer;
      opacity: 0.8;
    }

    .close-btn-review:hover {
      opacity: 0.9;
    }
  }

  input[type='file'] {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    margin: 0;
  }

  .media-helper-subtext {
    color: $gray-1-color;
  }
}


.guidelines {
  text-transform: capitalize;
}
.media-error-container,
.media-common-style {
  border-radius: rem-calc(5);
}
</style>